import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Criterion } from 'src/app/analysis/models/criterions/Criterion';
import { saveAnalysisInCacheAndRouteTo, saveTemplateInCacheAndRouteTo } from 'src/app/analysis/state/analysis.actions';
import { AngularJSUtils } from 'src/app/analysis/utils/angularjs-utils';
import { AppFeature } from 'src/app/core/app-feature/app-feature';
import { SeraEventsService } from '../sera-events/sera-events.service';
import { EventType } from '../sera-events/models/EventType';
import { AnalysisType } from 'src/app/analysis/models/AnalysisType';
import { AnalysisUtils } from 'src/app/analysis/utils/analysis-utils';
import { AnalysisPage } from 'src/app/analysis/models/AnalysisPage';

@Injectable({
    providedIn: 'root'
})
export class NavigationService {
    constructor(private readonly router: Router, private readonly store: Store, private readonly eventsService: SeraEventsService) {}

    public navigateToCriterion(
        criterion: Criterion,
        selectedPage: AnalysisPage,
        isReadMode: boolean,
        isTemplateAnalysis: boolean,
        analysisType: AnalysisType | undefined,
        appFeatures: AppFeature[] | null,
        route: ActivatedRoute
    ) {
        if (criterion.valueOf() !== selectedPage.valueOf()) {
            const isMigrated = AngularJSUtils.isMigratedCriterion(criterion) || AngularJSUtils.isMigratedCriteriaEnabled(criterion, appFeatures);
            const redirectUrl = AngularJSUtils.getCriterionUrl(criterion, isTemplateAnalysis);
            const action = isTemplateAnalysis ? saveTemplateInCacheAndRouteTo({ redirectUrl }) : saveAnalysisInCacheAndRouteTo({ redirectUrl });
            const navigateFn = isMigrated
                ? () => this.router.navigate([this.navigateToMigratedCriterion(criterion, route)])
                : () => this.store.dispatch(action);

            if (isReadMode || AnalysisUtils.isGroupAnalysis(analysisType) || this.selectedPageIsNotCriterion(selectedPage)) {
                navigateFn();
            } else {
                this.eventsService.fireEvent({ eventType: EventType.NavigateOutCriterion, afterFn: navigateFn });
            }
        }
    }

    public navigateToCriterionFromOverview(criterion: Criterion, isTemplateAnalysis: boolean, appFeatures: AppFeature[] | null, route: ActivatedRoute) {
        const isMigrated = AngularJSUtils.isMigratedCriterion(criterion) || AngularJSUtils.isMigratedCriteriaEnabled(criterion, appFeatures);
        const redirectUrl = AngularJSUtils.getCriterionUrl(criterion, isTemplateAnalysis);
        const action = isTemplateAnalysis ? saveTemplateInCacheAndRouteTo({ redirectUrl }) : saveAnalysisInCacheAndRouteTo({ redirectUrl });
        const navigateFn = isMigrated ? () => this.router.navigate([this.navigateToMigratedCriterion(criterion, route)]) : () => this.store.dispatch(action);

        navigateFn();
    }

    public navigateToAngularPage(
        route: string,
        isReadMode: boolean,
        isTemplateAnalysis: boolean,
        analysisType: AnalysisType | undefined,
        selectedPage: AnalysisPage | null,
        appFeatures: AppFeature[] | null,
        activeRoute: ActivatedRoute
    ) {
        const isMigrated = AngularJSUtils.isMigratedAnalysisPage(route, appFeatures);
        const isRedirectToTemplateHeader = route === 'HEADER' && isTemplateAnalysis;
        const action = AngularJSUtils.getAngularJSRouting(route, isTemplateAnalysis, null);
        const navigateFn =
            isMigrated && !isRedirectToTemplateHeader
                ? () => this.router.navigate([route.toLowerCase()], { relativeTo: activeRoute })
                : () => this.store.dispatch(action);

        isReadMode || AnalysisUtils.isGroupAnalysis(analysisType) || this.selectedPageIsNotCriterion(selectedPage)
            ? navigateFn()
            : this.eventsService.fireEvent({ eventType: EventType.NavigateOutCriterion, afterFn: navigateFn });
    }

    public navigateToMigratedCriterion(criterion: Criterion, route: ActivatedRoute): string {
        const criterionSuffix = this.buildCriterionSuffixForNavigation(criterion);

        const currentUrl = this.router.url;
        const baseUrl = this.extractBaseAnalysisUrl(currentUrl);

        if (!baseUrl || baseUrl === currentUrl) {
            const urlSegments = route.snapshot.url.slice(0, 4).map(segment => segment.path);
            return ['/analysis', ...urlSegments, criterionSuffix].join('/');
        }

        return baseUrl + '/' + criterionSuffix;
    }

    public extractBaseAnalysisUrl(url: string): string {
        const criterionPattern = /\/cr\d+(\/.*)?$/;
        const pagePattern = /\/(header|overview)(\/.*)?$/;

        let baseUrl = url.replace(criterionPattern, '');
        baseUrl = baseUrl.replace(pagePattern, '');

        return baseUrl;
    }

    public redirectBasedOnAnalysisType(redirectPage: AnalysisPage) {
        const currentCriterion = this.convertUrlToCriterionEnum(this.router.url);
        if (currentCriterion < redirectPage) {
            const redirectCriterion = AnalysisPage.getCriterion(redirectPage) as Criterion;
            this.router.navigate([
                this.router.url.replace('cr' + currentCriterion.valueOf().toString(), this.buildCriterionSuffixForNavigation(redirectCriterion))
            ]);
        }
    }

    public convertUrlToCriterionEnum(url: string): Criterion {
        const match = RegExp(/\/cr(\d+)(\/|$)/).exec(url);
        if (match) {
            const numberStr = match[1].padStart(2, '0');
            const enumKey = `MM${numberStr}` as keyof typeof Criterion;
            return Criterion[enumKey];
        }
        return Criterion.MM01;
    }

    public buildCriterionSuffixForNavigation(criterion: Criterion): string {
        return criterion !== Criterion.MM13 ? 'cr' + criterion : 'cr' + criterion + '/allhazards';
    }

    private selectedPageIsNotCriterion(page: AnalysisPage | null): boolean {
        return page === AnalysisPage.HEADER || page === AnalysisPage.OVERVIEW;
    }
}
